import type { Activity, Transition, Participant, Workflow, ActivityType, ActivityTool, ToolsConfig, ToolDefinition, FormalParameter, WorkflowBinding } from './types'
import { DEFAULT_ACTIVITY_WIDTH, DEFAULT_ACTIVITY_HEIGHT } from './constants'

// Twasta ExtendedAttribute names — EXACT parity with the Python engine.
const ATTR_PROFILE = 'TWASTA_PROFILE'
const ATTR_BIND_OBJECT = 'TWASTA_BIND_OBJECT'
const ATTR_BIND_TRIGGER = 'TWASTA_BIND_TRIGGER'
const ATTR_END = 'TWASTA_END'
const ATTR_ON = 'TWASTA_ON'
const ATTR_USERS = 'TWASTA_USERS'

interface ParseResult {
  workflow: Workflow
  participants: Participant[]
  objectName: string
  toolsConfig: ToolsConfig
  binding: WorkflowBinding
}

/** Parse an XPDL 1.0 string into workflow state. */
export function parseXpdl(xpdlString: string): ParseResult {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xpdlString, 'text/xml')

  const parserError = xmlDoc.querySelector('parsererror')
  if (parserError) {
    throw new Error('Invalid XPDL XML')
  }

  const objectName = xmlDoc.documentElement.getAttribute('Id') || 'workflow'

  const participants = parseParticipants(xmlDoc)
  const toolsConfig = parseApplications(xmlDoc)
  const activities = parseActivities(xmlDoc, toolsConfig)
  const transitions = parseTransitions(xmlDoc)
  const binding = parseBinding(xmlDoc)

  // Apply layout information from ExtendedAttributes
  applyLayoutInfo(xmlDoc, activities)

  // Ensure there's a start activity
  const hasStart = activities.some(a => a.type === 'start')
  if (!hasStart) {
    activities.unshift({
      id: 'start',
      type: 'start',
      description: 'Start',
      x: 50,
      y: 50,
      width: DEFAULT_ACTIVITY_WIDTH,
      height: DEFAULT_ACTIVITY_HEIGHT,
      performer: '',
      tool: null,
    })
  }

  return {
    workflow: { activities, transitions },
    participants,
    objectName,
    toolsConfig,
    binding,
  }
}

/** Read process-level Twasta binding markers (TWASTA_PROFILE / BIND_OBJECT /
 *  BIND_TRIGGER) from the WorkflowProcess's own ExtendedAttributes. Returns an
 *  empty object when none are present (a plain non-approval workflow). */
function parseBinding(xmlDoc: Document): WorkflowBinding {
  const binding: WorkflowBinding = {}
  // The process-level ExtendedAttributes block is a DIRECT child of
  // WorkflowProcess (distinct from per-activity / per-transition / per-
  // participant blocks). Selecting it via proc.children keeps the nested blocks
  // out of scope; the TWASTA_* markers live as ExtendedAttribute rows inside it.
  const proc = xmlDoc.querySelector('WorkflowProcess')
  if (!proc) return binding
  // Only the process's OWN ExtendedAttributes (not those nested in
  // Activities / Transitions / Participants children).
  const block = Array.from(proc.children).find(c => c.tagName === 'ExtendedAttributes')
  if (!block) return binding
  Array.from(block.children).forEach(attr => {
    if (attr.tagName !== 'ExtendedAttribute') return
    const name = attr.getAttribute('Name')
    const value = attr.getAttribute('Value') || ''
    if (name === ATTR_PROFILE) {
      const v = value.trim().toLowerCase()
      if (v === 'linear' || v === 'advanced') binding.profile = v
    } else if (name === ATTR_BIND_OBJECT) {
      if (value.trim()) binding.bindObject = value.trim()
    } else if (name === ATTR_BIND_TRIGGER) {
      const trigs = value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      if (trigs.length > 0) binding.bindTriggers = trigs
    }
  })
  return binding
}

/** Parse <Applications> section from XPDL into ToolsConfig. */
function parseApplications(xmlDoc: Document): ToolsConfig {
  const tools: ToolDefinition[] = []
  const elements = xmlDoc.querySelectorAll('Applications > Application')

  elements.forEach(el => {
    const id = el.getAttribute('Id') || ''
    const name = el.getAttribute('Name') || id
    const desc = el.querySelector('Description')?.textContent || ''

    // Get type and icon from ExtendedAttributes
    let type = 'Application'
    let icon = ''
    const extAttrs = el.querySelectorAll('ExtendedAttributes > ExtendedAttribute')
    extAttrs.forEach(attr => {
      if (attr.getAttribute('Name') === 'APPLN_TYPE') {
        type = attr.getAttribute('Value') || 'Application'
      }
      if (attr.getAttribute('Name') === 'APPLN_ICON') {
        icon = attr.getAttribute('Value') || ''
      }
    })

    // Parse FormalParameters
    const formalParams: FormalParameter[] = []
    const fpElements = el.querySelectorAll('FormalParameters > FormalParameter')
    fpElements.forEach(fp => {
      const basicType = fp.querySelector('DataType > BasicType')
      formalParams.push({
        Id: fp.getAttribute('Id') || '',
        Index: parseInt(fp.getAttribute('Index') || '0') || undefined,
        Mode: fp.getAttribute('Mode') || 'IN',
        DataType: basicType ? {
          BasicType: { Type: basicType.getAttribute('Type') || 'STRING' },
        } : undefined,
      })
    })

    const tool: ToolDefinition = {
      Id: id,
      Name: name,
      Type: type,
      Description: desc,
      Icon: icon || undefined,
    }

    if (formalParams.length > 0) {
      tool.FormalParameters = { FormalParameter: formalParams }
    }

    tools.push(tool)
  })

  return tools.length > 0 ? { Tools: { Tool: tools } } : {}
}

function parseParticipants(xmlDoc: Document): Participant[] {
  const participants: Participant[] = []
  const elements = xmlDoc.querySelectorAll('Participants > Participant')

  elements.forEach(el => {
    const typeEl = el.querySelector('ParticipantType')
    const pType = (typeEl?.getAttribute('Type') || 'ROLE') as 'ROLE' | 'SYSTEM'

    const participant: Participant = {
      id: el.getAttribute('Id') || '',
      name: el.getAttribute('Name') || '',
      type: pType,
    }

    if (pType === 'ROLE') {
      const attrs = el.querySelectorAll('ExtendedAttributes > ExtendedAttribute')
      attrs.forEach(attr => {
        const name = attr.getAttribute('Name')
        const value = attr.getAttribute('Value') || ''
        switch (name) {
          case 'PRFMR_ROLE_CODE':
            participant.roleCode = value
            break
          case 'SIGN_FOR':
            participant.participatesFor = value
            break
          case 'PRFMR_ROLE_EXPR':
            participant.roleExpression = value
            break
          case 'PRFMR_ROLE_EXPR_TYPE':
            participant.roleType = value
            break
          case ATTR_USERS: {
            const users = value.split(',').map(u => u.trim()).filter(Boolean)
            if (users.length > 0) participant.users = users
            break
          }
        }
      })
    }

    participants.push(participant)
  })

  return participants
}

function determineActivityType(el: Element): ActivityType {
  const eventEl = el.querySelector('Event')
  if (eventEl) {
    if (eventEl.querySelector('StartEvent')) return 'start'
    if (eventEl.querySelector('EndEvent')) return 'end'
  }
  return 'activity'
}

function parseActivities(xmlDoc: Document, toolsConfig?: ToolsConfig): Activity[] {
  const activities: Activity[] = []
  const elements = xmlDoc.querySelectorAll('Activities > Activity')

  elements.forEach(el => {
    const id = el.getAttribute('Id') || ''
    const name = el.getAttribute('Name') || ''
    const type = determineActivityType(el)

    // Position from inline ExtendedAttribute
    let x = 0, y = 0
    const extAttrs = el.querySelectorAll('ExtendedAttributes > ExtendedAttribute')
    extAttrs.forEach(attr => {
      if (attr.getAttribute('Name') === 'NodeGraphicsInfo') {
        x = parseInt(attr.getAttribute('XCoordinate') || '0') || 0
        y = parseInt(attr.getAttribute('YCoordinate') || '0') || 0
      }
    })

    // Parse icon + approval end-kind from this activity's ExtendedAttributes.
    let icon = ''
    let endKind: 'approved' | 'rejected' | undefined
    extAttrs.forEach(attr => {
      const aName = attr.getAttribute('Name')
      if (aName === 'ActivityIcon') {
        icon = attr.getAttribute('Value') || ''
      } else if (aName === ATTR_END) {
        const v = (attr.getAttribute('Value') || '').trim().toLowerCase()
        if (v === 'approved' || v === 'rejected') endKind = v
      }
    })

    const activity: Activity = {
      id,
      type,
      description: name,
      x,
      y,
      width: DEFAULT_ACTIVITY_WIDTH,
      height: DEFAULT_ACTIVITY_HEIGHT,
      performer: '',
      tool: null,
      icon,
      ...(type === 'end' && endKind ? { endKind } : {}),
    }

    if (type === 'activity') {
      const performerEl = el.querySelector('Performer')
      if (performerEl) {
        activity.performer = performerEl.textContent || ''
      }

      const impl = el.querySelector('Implementation')
      if (impl) {
        activity.tool = parseToolInfo(impl, toolsConfig)
      }
    }

    // Start activities go first
    if (type === 'start') {
      // Remove any existing start
      const existingIdx = activities.findIndex(a => a.type === 'start')
      if (existingIdx >= 0) activities.splice(existingIdx, 1)
      activities.unshift(activity)
    } else {
      activities.push(activity)
    }
  })

  return activities
}

function parseToolInfo(implEl: Element, toolsConfig?: ToolsConfig): ActivityTool | null {
  const toolEl = implEl.querySelector('Tool')
  if (!toolEl) return null

  const toolId = toolEl.getAttribute('Id') || ''
  const toolDef = toolsConfig?.Tools?.Tool.find(t => t.Id === toolId)

  const tool: ActivityTool = {
    type: toolDef?.Type || 'Application',
    name: toolId,
    parameters: [],
  }

  const actualParams = Array.from(toolEl.querySelectorAll('ActualParameter'))

  if (toolDef?.FormalParameters?.FormalParameter) {
    tool.parameters = toolDef.FormalParameters.FormalParameter.map((fp, idx) => ({
      id: fp.Id,
      value: actualParams[idx]?.textContent || fp.DefaultValue || '',
      mode: fp.Mode || 'IN',
      dataType: fp.DataType?.BasicType?.Type || 'STRING',
    }))
  } else if (actualParams.length > 0) {
    tool.parameters = actualParams.map((p, idx) => ({
      id: `param_${idx + 1}`,
      value: p.textContent || '',
      mode: 'IN',
      dataType: 'STRING',
    }))
  }

  return tool
}

function parseTransitions(xmlDoc: Document): Transition[] {
  const transitions: Transition[] = []
  const elements = xmlDoc.querySelectorAll('Transitions > Transition')

  elements.forEach(el => {
    const condEl = el.querySelector('Condition')
    const condition = condEl?.textContent?.trim() || ''
    const rawCondType = (condEl?.getAttribute('Type') || '').trim().toUpperCase()
    const conditionType: 'CONDITION' | 'OTHERWISE' =
      rawCondType === 'OTHERWISE' ? 'OTHERWISE' : 'CONDITION'

    // Decision channel from TWASTA_ON (default 'approve').
    let on: 'approve' | 'reject' = 'approve'
    const onAttr = Array.from(el.querySelectorAll('ExtendedAttributes > ExtendedAttribute'))
      .find(a => a.getAttribute('Name') === ATTR_ON)
    if (onAttr) {
      const v = (onAttr.getAttribute('Value') || '').trim().toLowerCase()
      if (v === 'reject') on = 'reject'
    }

    transitions.push({
      id: el.getAttribute('Id') || '',
      from: el.getAttribute('From') || '',
      to: el.getAttribute('To') || '',
      condition,
      description: el.getAttribute('Name') || '',
      on,
      conditionType,
    })
  })

  return transitions
}

function applyLayoutInfo(xmlDoc: Document, activities: Activity[]) {
  // Layout from process-level ExtendedAttributes
  const layoutAttrs = xmlDoc.querySelectorAll('ExtendedAttribute[Name="NodeGraphicsInfo"]')
  layoutAttrs.forEach(info => {
    const activityId = info.getAttribute('ToolId')
    if (!activityId) return
    const activity = activities.find(a => a.id === activityId)
    if (activity) {
      activity.x = parseInt(info.getAttribute('XCoordinate') || '0') || activity.x
      activity.y = parseInt(info.getAttribute('YCoordinate') || '0') || activity.y
    }
  })
}
